import { Divider } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { FacetFilterInput, FacetMetadata } from '../../../types.generated';
import {
    ENTITY_INDEX_FILTER_NAME,
    ORIGIN_FILTER_NAME,
    TYPE_NAMES_FILTER_NAME,
    LEGACY_ENTITY_FILTER_NAME,
    BROWSE_PATH_V2_FILTER_NAME,
} from '../utils/constants';
import ActiveFilter from './ActiveFilter';
import { SORTED_FILTERS } from './constants';
import MoreFilters from './MoreFilters';
import SearchFilter from './SearchFilter';
import { TextButton } from './styledComponents';
import { sortFacets } from './utils';
import {
    SEARCH_RESULTS_ADVANCED_SEARCH_ID,
    SEARCH_RESULTS_FILTERS_ID,
} from '../../onboarding/config/SearchOnboardingConfig';
import { useFilterRendererRegistry } from './render/useFilterRenderer';
import { useTranslation } from '../../../i18n/useTranslation';
import { FilterScenarioType } from './render/types';

const NUM_VISIBLE_FILTER_DROPDOWNS = 5;

export const FlexWrapper = styled.div`
    gap: 8px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    > button {
        margin-right: 0;

        @media (max-width: 576px) {
            flex: 1 1 100%;
        }
    }
`;

export const FlexSpacer = styled.div`
    display: flex;
    justify-content: space-between;

    @media (max-width: 991px) {
        gap: 12px;
        flex-wrap: wrap;
    }
`;

const StyledDivider = styled(Divider)`
    margin: 8px 0;
`;

export const FilterButtonsWrapper = styled.div`
    display: flex;
    flex-wrap: nowrap;
`;

// remove legacy filter options as well as new _index and browsePathV2 filter from dropdowns
const FILTERS_TO_REMOVE = [
    TYPE_NAMES_FILTER_NAME,
    LEGACY_ENTITY_FILTER_NAME,
    ENTITY_INDEX_FILTER_NAME,
    BROWSE_PATH_V2_FILTER_NAME,
];

interface Props {
    availableFilters: FacetMetadata[] | null;
    activeFilters: FacetFilterInput[];
    onChangeFilters: (newFilters: FacetFilterInput[]) => void;
    onClearFilters: () => void;
    showAdvancedFilters: () => void;
}

export default function BasicFilters({
    availableFilters,
    activeFilters,
    onChangeFilters,
    onClearFilters,
    showAdvancedFilters,
}: Props) {
    const { t } = useTranslation();
    // only want Environment filter if there's 2 or more envs
    const filters = availableFilters
        ?.filter((f) => (f.field === ORIGIN_FILTER_NAME ? f.aggregations.length >= 2 : true)) // only want Environment filter if there's 2 or more envs
        .filter((f) => !FILTERS_TO_REMOVE.includes(f.field))
        .sort((facetA, facetB) => sortFacets(facetA, facetB, SORTED_FILTERS));
    // if there will only be one filter in the "More Filters" dropdown, show that filter instead
    const shouldShowMoreDropdown = filters && filters.length > NUM_VISIBLE_FILTER_DROPDOWNS + 1;
    const visibleFilters = shouldShowMoreDropdown ? filters?.slice(0, NUM_VISIBLE_FILTER_DROPDOWNS) : filters;
    const hiddenFilters = shouldShowMoreDropdown ? filters?.slice(NUM_VISIBLE_FILTER_DROPDOWNS) : [];
    const filterRendererRegistry = useFilterRendererRegistry();

    // Update filters to replace '__datahub_system' username with 'System'
    const filtersToRender = visibleFilters?.map((filter) => {
        if (filter.field === 'owners') {
            return {
                ...filter,
                aggregations: filter.aggregations.map((agg: any) => {
                    if (agg.entity?.username === '__datahub_system') {
                        return {
                            ...agg,
                            entity: {
                                ...agg.entity,
                                username: 'System',
                            },
                        };
                    }
                    return agg;
                }),
            };
        }
        return filter;
    });

    return (
        <span id={SEARCH_RESULTS_FILTERS_ID}>
            <FlexSpacer>
                <FlexWrapper>
                    {filtersToRender?.map((filter) => {
                        return filterRendererRegistry.hasRenderer(filter.field) ? (
                            filterRendererRegistry.render(filter.field, {
                                scenario: FilterScenarioType.SEARCH_V2_PRIMARY,
                                filter,
                                activeFilters,
                                onChangeFilters,
                            })
                        ) : (
                            <SearchFilter
                                key={filter.field}
                                filter={filter}
                                activeFilters={activeFilters}
                                onChangeFilters={onChangeFilters}
                            />
                        );
                    })}
                    {hiddenFilters && hiddenFilters.length > 0 && (
                        <MoreFilters
                            filters={hiddenFilters}
                            activeFilters={activeFilters}
                            onChangeFilters={onChangeFilters}
                        />
                    )}
                </FlexWrapper>
                <FilterButtonsWrapper>
                    <TextButton
                        id={SEARCH_RESULTS_ADVANCED_SEARCH_ID}
                        type="text"
                        onClick={showAdvancedFilters}
                        marginTop={0}
                    >
                        {t('Advanced Filters')}
                    </TextButton>
                </FilterButtonsWrapper>
            </FlexSpacer>
            {activeFilters.length > 0 && (
                <>
                    <StyledDivider />
                    <FlexSpacer>
                        <FlexWrapper>
                            {activeFilters.map((activeFilter) => (
                                <ActiveFilter
                                    key={activeFilter.field}
                                    filter={activeFilter}
                                    availableFilters={availableFilters}
                                    activeFilters={activeFilters}
                                    onChangeFilters={onChangeFilters}
                                />
                            ))}
                        </FlexWrapper>
                        <TextButton
                            type="text"
                            className="mt-0"
                            onClick={onClearFilters}
                            data-testid="clear-all-filters"
                        >
                            {t('clear all')}
                        </TextButton>
                    </FlexSpacer>
                </>
            )}
        </span>
    );
}
